/**
 * lazyWithPreload — React.lazy plus an imperative `.preload()` method.
 *
 * Normal React.lazy only starts loading a chunk when the component renders. By
 * exposing `.preload()` we can warm the chunk earlier — on link hover/focus
 * (see PrefetchLink) or during idle time — so navigation feels instant while the
 * code still ships as a separate, lazily-loaded bundle.
 *
 * Once the module has arrived (preloaded, or loaded once already) the component
 * renders it DIRECTLY, without going through React.lazy. React.lazy suspends
 * on its first render even when the module is already in memory (its promise
 * still resolves a tick later), which showed a loading state for a moment —
 * the "blink" on a page's first visit even after hovering its link.
 */
import { createElement, lazy, useState } from 'react';

export function lazyWithPreload(factory) {
  /** The loaded module, once it has arrived. */
  let loaded = null;
  let pending = null;

  const load = () => {
    if (!pending) {
      pending = factory().then((module) => {
        loaded = module;
        return module;
      });
      // A failed download must be retryable on the next attempt.
      pending.catch(() => {
        pending = null;
      });
    }
    return pending;
  };

  const LazyComponent = lazy(load);

  function PreloadableComponent(props) {
    // Decided ONCE per mounted instance. Switching from the lazy wrapper to the
    // real component mid-life would change the element type, which React
    // treats as a new component — the page would remount and lose its state
    // (a half-filled form). So: already loaded at mount -> direct; otherwise
    // stay on the lazy wrapper for this instance's whole life.
    const [Direct] = useState(() => loaded?.default ?? null);
    return Direct ? createElement(Direct, props) : createElement(LazyComponent, props);
  }

  PreloadableComponent.preload = load;
  return PreloadableComponent;
}

/** Preload a batch of lazy components when the browser is idle. */
export function preloadWhenIdle(components) {
  const run = () => components.forEach((c) => c?.preload?.());
  if (typeof window !== 'undefined' && 'requestIdleCallback' in window) {
    window.requestIdleCallback(run, { timeout: 2000 });
  } else {
    setTimeout(run, 1200);
  }
}
